import { useState, useEffect, useCallback } from 'react'
import Editor from '@monaco-editor/react'
import { Button, Input, Textarea, Toggle, Alert, ConfirmDialog } from '@/ui'
import { apiFetch } from '@/lib/api'
import { useToast } from '../../components/ToastContainer'
import type { Stylesheet } from './types'
import styles from '../SectionDesigns.module.css'

interface Props {
  stylesheet: Stylesheet | null // null = 새 스타일시트 생성 모드
  onSaved: () => void
  onDeleted: () => void
}

export function StylesheetPanel({ stylesheet, onSaved, onDeleted }: Props) {
  const { addToast } = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [content, setContent] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    setName(stylesheet?.name ?? '')
    setDescription(stylesheet?.description ?? '')
    setContent(stylesheet?.content ?? '')
    setIsActive(stylesheet?.isActive ?? true)
  }, [stylesheet])

  const handleSave = useCallback(async () => {
    if (!name.trim() || !content.trim()) {
      addToast({ type: 'error', message: '이름과 CSS 내용은 필수입니다' })
      return
    }
    setSaving(true)
    try {
      const body = JSON.stringify({
        name: name.trim(),
        type: 'css',
        content,
        description: description.trim() || null,
        isActive,
      })
      if (stylesheet) {
        await apiFetch(`/api/admin/stylesheets/${stylesheet.id}`, { method: 'PUT', body })
      } else {
        await apiFetch('/api/admin/stylesheets', { method: 'POST', body })
      }
      addToast({ type: 'success', message: '저장 완료' })
      onSaved()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '저장 실패'
      addToast({ type: 'error', message })
    } finally {
      setSaving(false)
    }
  }, [name, content, description, isActive, stylesheet, addToast, onSaved])

  const handleDelete = useCallback(async () => {
    if (!stylesheet) return
    setDeleting(true)
    try {
      await apiFetch(`/api/admin/stylesheets/${stylesheet.id}`, { method: 'DELETE' })
      addToast({ type: 'success', message: `"${stylesheet.name}" 삭제 완료` })
      setShowDelete(false)
      onDeleted()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '삭제 실패'
      addToast({ type: 'error', message })
    } finally {
      setDeleting(false)
    }
  }, [stylesheet, addToast, onDeleted])

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>
          {stylesheet ? '전역 스타일시트 편집' : '새 전역 스타일시트'}
        </h2>
        <div className={styles.panelActions}>
          <Button variant="outline" size="sm" onClick={handleSave} disabled={saving}>
            {saving ? '저장 중...' : '저장'}
          </Button>
          {stylesheet && (
            <Button variant="outline" size="sm" onClick={() => setShowDelete(true)}>
              삭제
            </Button>
          )}
        </div>
      </div>

      <Alert variant="warning">
        스코프 안 함(전역) · 저장 시 이 시트를 참조하는 모든 템플릿에 전파됩니다.
      </Alert>

      <div className={styles.metaGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>이름</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="예: t7-global" />
        </label>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>설명</span>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </label>
        <div className={styles.fieldInline}>
          {/* Toggle.onChange는 (checked: boolean) → void 시그니처 */}
          <Toggle checked={isActive} onChange={setIsActive} label="활성" size="sm" />
        </div>
      </div>

      <div className={styles.editorFull}>
        <Editor
          height="100%"
          defaultLanguage="css"
          language="css"
          value={content}
          onChange={(v) => setContent(v ?? '')}
          theme="vs-dark"
          options={{ minimap: { enabled: false }, fontSize: 13, wordWrap: 'on', tabSize: 2 }}
        />
      </div>

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="스타일시트 삭제"
        message={`"${stylesheet?.name}" 스타일시트를 삭제하시겠습니까? 참조하는 템플릿이 깨질 수 있습니다.`}
        confirmLabel="삭제"
        variant="danger"
        loading={deleting}
      />
    </div>
  )
}
